import { useCallback, useMemo, useReducer } from 'react';

import { instanceOfGeneralRequestResponse } from '@utils/typeGuards';

import { IHttpClientResponse, IRequestParam, IUseHttpClientParams } from '../model';

import { setSuccessWithWarning, setRequestFailed, setReset, setSuccess } from './asyncHttpClientReducer/actions';
import { HttpClientReducer } from './asyncHttpClientReducer/model';
import { initialState, reducer } from './asyncHttpClientReducer/reducer';
import { useHttpClientAwaitable } from './useHttpClientAwaitable';

export const useHttpClientAsync = <TResult, TParam, TRequestResult = TResult>(
  clientParams: IUseHttpClientParams<TResult, TRequestResult>,
): IHttpClientResponse<TResult, TParam> => {
  const [state, dispatch] = useReducer<HttpClientReducer<TResult>>(reducer, initialState);

  const { request: awaitableRequest } = useHttpClientAwaitable<TResult, TParam, TRequestResult>(clientParams);

  const request = useCallback(
    async (params?: IRequestParam<TParam>) => {
      dispatch(setReset());

      const requestResponse = await awaitableRequest(params);

      if (instanceOfGeneralRequestResponse<TResult>(requestResponse)) {
        return dispatch(setSuccessWithWarning(requestResponse, requestResponse.messages));
      }

      if (requestResponse) {
        return dispatch(setSuccess(requestResponse));
      }

      return dispatch(setRequestFailed());
    },
    [awaitableRequest, dispatch],
  );

  return useMemo(() => {
    return { loading: state.loading, error: state.error, response: state.data, request };
  }, [state, request]);
};
